import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, FileText, Video } from "lucide-react";
import { useDropzone } from "react-dropzone";
import {
  getMedia as getMediaList,
  deleteMedia,
  uploadMedia,
} from "../../../services/admin/media";
import toast from "react-hot-toast";

interface Media {
  id: number;
  filename: string;
  url: string; // full URL now
}

function AdminMedia() {
  const [mediaList, setMediaList] = useState<Media[]>([]);
  const [visibleMedia, setVisibleMedia] = useState<Media[]>([]);
  const [selectedMediaIds, setSelectedMediaIds] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit] = useState(32); // items per page

  const loadMedia = useCallback(async () => {
    const res = await getMediaList();
    if (res.success) {
      // Expect API to already return full URLs
      const reversedData = [...res.data].reverse(); // newest first
      setMediaList(reversedData);
      setVisibleMedia(reversedData.slice(0, limit)); // First page
    }
  }, [limit]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  // Update visible media when page changes
  useEffect(() => {
    setVisibleMedia(mediaList.slice(0, page * limit));
  }, [page, mediaList, limit]);

  const handleSelect = (id: number) => {
    setSelectedMediaIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedMediaIds.length} file(s)?`
    );
    if (!confirmDelete) return;

    for (const id of selectedMediaIds) {
      await deleteMedia(id);
    }

    toast.success("Selected media deleted.");
    setSelectedMediaIds([]);
    loadMedia();
  };

  const handleUpload = async () => {
    if (!uploadQueue.length) return;

    setUploading(true);
    const res = await uploadMedia(uploadQueue);
    setUploading(false);
    setUploadQueue([]);
    setIsModalOpen(false);

    if (res.success) {
      toast.success("Upload completed successfully!");
      loadMedia();
    } else {
      toast.error("Upload failed: " + res.error);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [],
      "application/pdf": [],
      "video/*": [],
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      setUploadQueue((prev) => [...prev, ...acceptedFiles]);
    },
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">Media</h1>
        <div className="flex gap-4">
          {selectedMediaIds.length > 0 && (
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
            >
              <Trash2 size={16} />
              Delete
            </button>
          )}
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-[#009FE3] text-white px-4 py-2 rounded hover:bg-[#0085c5] transition"
          >
            <Plus size={18} className="inline mr-2" />
            Add Media
          </button>
        </div>
      </div>

      {/* Media Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {visibleMedia.map((media) => (
          <div
            key={media.id}
            className={`relative border rounded cursor-pointer transition-all duration-200 hover:shadow-md ${
              selectedMediaIds.includes(media.id) ? "ring-2 ring-[#009FE3]" : ""
            }`}
            onClick={() => handleSelect(media.id)}
          >
            {media.filename.toLowerCase().endsWith(".pdf") ? (
              <div className="w-full h-24 flex items-center justify-center bg-[#009FE3] rounded">
                <FileText size={40} className="text-white" />
              </div>
            ) : media.filename.toLowerCase().match(/\.(mp4|webm|ogg)$/) ? (
              <div className="w-full h-24 flex items-center justify-center bg-[#009FE3] rounded">
                <Video size={40} className="text-white" />
              </div>
            ) : (
              <img
                src={media.url} // ✅ already full URL
                alt={media.filename}
                className="w-full h-24 object-cover rounded"
              />
            )}
            <p
              className="text-sm text-center truncate p-2 border-t border-[#f5f5f5] bg-white"
              title={media.filename}
            >
              {media.filename}
            </p>
            {selectedMediaIds.includes(media.id) && (
              <div className="absolute inset-0 bg-[#009FE3]/40 rounded flex items-center justify-center">
                <span className="text-white font-bold text-lg">✓</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {visibleMedia.length < mediaList.length && (
        <div className="flex justify-center mt-6">
          <button
            className="px-4 py-2 bg-[#009FE3] text-white rounded hover:bg-[#0085c5]"
            onClick={() => setPage((prev) => prev + 1)}
          >
            Load More
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md relative">
            <h2 className="text-lg font-semibold mb-4">Upload Media</h2>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed p-6 rounded-lg text-center cursor-pointer ${
                isDragActive
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300"
              }`}
            >
              <input {...getInputProps()} />
              <p className="text-gray-500">
                {isDragActive
                  ? "Drop the files here..."
                  : "Drag & drop or click to select files"}
              </p>
            </div>

            {uploadQueue.length > 0 && (
              <ul className="mt-4 space-y-1 max-h-32 overflow-y-auto text-sm text-gray-700">
                {uploadQueue.map((file) => (
                  <li key={file.name + file.lastModified}>{file.name}</li>
                ))}
              </ul>
            )}

            <div className="flex justify-end mt-6 gap-2">
              <button
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
                onClick={() => {
                  setIsModalOpen(false);
                  setUploadQueue([]);
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-[#009FE3] text-white rounded hover:bg-[#0085c5]"
                onClick={handleUpload}
                disabled={uploading || !uploadQueue.length}
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMedia;
